/**
 * Address List Component
 *
 * Displays addresses for an organization in a collapsible table with:
 * - Search by address name or city
 * - Pagination with 10 items per page
 * - Edit button (enabled only for active addresses)
 * - View button (enabled for all addresses)
 * - Cascading archive behavior (all addresses archived when org is archived)
 */

import * as React from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/contexts/ToastContext'
import {
  Search,
  Edit,
  Eye,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Plus,
} from 'lucide-react'
import {
  AddOrganizationAddressComposer,
  EditOrganizationAddressComposer,
} from '@/forms/organizations'
import type { Address } from '@/types/organizations'

// ============================================================================
// Component Props
// ============================================================================

export interface AddressListProps {
  organizationId: string
  addresses: Address[]
  isArchived: boolean // Organization archive status
  onAddressChange?: () => void // Callback to refetch organization data
}

// ============================================================================
// Component Implementation
// ============================================================================

export function AddressList(props: AddressListProps) {
  const { organizationId, addresses = [], isArchived, onAddressChange } = props
  const { toast } = useToast()

  // State management
  const [searchQuery, setSearchQuery] = React.useState('')
  const [currentPage, setCurrentPage] = React.useState(1)
  const [isOpen, setIsOpen] = React.useState(true)

  // Dialog state
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false)
  const [isViewDialogOpen, setIsViewDialogOpen] = React.useState(false)
  const [selectedAddress, setSelectedAddress] = React.useState<Address | null>(null)

  const itemsPerPage = 10

  // Filter addresses by search query (name or city)
  const filteredAddresses = React.useMemo(() => {
    if (!searchQuery.trim()) return addresses

    const query = searchQuery.toLowerCase()
    return addresses.filter(
      (address) =>
        address.addressName.toLowerCase().includes(query) ||
        address.city.toLowerCase().includes(query)
    )
  }, [addresses, searchQuery])

  // Pagination
  const totalPages = Math.ceil(filteredAddresses.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedAddresses = filteredAddresses.slice(startIndex, endIndex)

  // Reset to page 1 when search query changes
  React.useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery])

  // Handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1))
  }

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
  }

  const handleAddClick = () => {
    setIsAddDialogOpen(true)
  }

  const handleEditClick = (address: Address) => {
    setSelectedAddress(address)
    setIsEditDialogOpen(true)
  }

  const handleViewClick = (address: Address) => {
    setSelectedAddress(address)
    setIsViewDialogOpen(true)
  }

  const handleAddSuccess = () => {
    setIsAddDialogOpen(false)
    onAddressChange?.()
    toast({
      title: 'Success',
      description: 'Address has been created successfully.',
      type: 'success',
    })
  }

  const handleAddError = (error: Error) => {
    toast({
      title: 'Error',
      description: error.message || 'Failed to create address.',
      type: 'error',
    })
  }

  const handleEditSuccess = () => {
    setIsEditDialogOpen(false)
    setSelectedAddress(null)
    onAddressChange?.()
    toast({
      title: 'Success',
      description: 'Address has been updated successfully.',
      type: 'success',
    })
  }

  const handleEditError = (error: Error) => {
    toast({
      title: 'Error',
      description: error.message || 'Failed to update address.',
      type: 'error',
    })
  }

  const handleCloseDialogs = () => {
    setIsAddDialogOpen(false)
    setIsEditDialogOpen(false)
    setIsViewDialogOpen(false)
    setSelectedAddress(null)
  }

  // Empty state
  if (addresses.length === 0) {
    return (
      <Card className="border shadow-sm">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle>Addresses (0)</CardTitle>
            </div>
            {!isArchived && organizationId && (
              <Button onClick={handleAddClick} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Address
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            No addresses found.
          </p>
        </CardContent>

        {/* Add Address Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Address</DialogTitle>
              <DialogDescription>Enter the address details below.</DialogDescription>
            </DialogHeader>
            <AddOrganizationAddressComposer
              organizationId={organizationId}
              onSuccess={handleAddSuccess}
              onError={handleAddError}
              onCancel={handleCloseDialogs}
              showHeader={false}
            />
          </DialogContent>
        </Dialog>
      </Card>
    )
  }

  // Main render with addresses
  return (
    <>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <Card className="border shadow-sm">
          <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="p-0 h-auto">
                    {isOpen ? (
                      <ChevronUp className="h-5 w-5" />
                    ) : (
                      <ChevronDown className="h-5 w-5" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CardTitle>Addresses ({addresses.length})</CardTitle>
              </div>
              {!isArchived && organizationId && (
                <Button onClick={handleAddClick} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Address
                </Button>
              )}
            </div>
          </CardHeader>

          <CollapsibleContent>
            <CardContent>
              {/* Search Section */}
              <div className="mb-4">
                <div className="relative max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search addresses..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Table Section */}
              {filteredAddresses.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No addresses found.
                </p>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-left">Address Name</TableHead>
                          <TableHead className="text-center">Type</TableHead>
                          <TableHead className="text-center">Phone</TableHead>
                          <TableHead className="text-left">Street</TableHead>
                          <TableHead className="text-left">City</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedAddresses.map((address) => (
                          <TableRow key={address.id} className="hover:bg-muted/50">
                            <TableCell className="font-medium">
                              {address.addressName}
                            </TableCell>
                            <TableCell className="text-center">
                              {address.addressType}
                            </TableCell>
                            <TableCell className="text-center">
                              {address.phone || '—'}
                            </TableCell>
                            <TableCell>{address.street}</TableCell>
                            <TableCell>{address.city}</TableCell>
                            <TableCell className="text-center">
                              <Badge
                                variant={address.isArchived ? 'secondary' : 'default'}
                              >
                                {address.isArchived ? 'Archived' : 'Active'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                {!address.isArchived && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditClick(address)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewClick(address)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination Section */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-muted-foreground">
                        Showing {startIndex + 1} to{' '}
                        {Math.min(endIndex, filteredAddresses.length)} of{' '}
                        {filteredAddresses.length} addresses
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Previous
                        </Button>
                        <span className="text-sm">
                          Page {currentPage} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                        >
                          Next
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Add Address Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Address</DialogTitle>
            <DialogDescription>Enter the address details below.</DialogDescription>
          </DialogHeader>
          <AddOrganizationAddressComposer
            organizationId={organizationId}
            onSuccess={handleAddSuccess}
            onError={handleAddError}
            onCancel={handleCloseDialogs}
            showHeader={false}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Address Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Address</DialogTitle>
            <DialogDescription>Update address details below.</DialogDescription>
          </DialogHeader>
          {selectedAddress && (
            <EditOrganizationAddressComposer
              organizationId={organizationId}
              initialValues={{
                ...selectedAddress,
                street: selectedAddress.street ?? selectedAddress.addressLine1 ?? '',
              }}
              onSuccess={handleEditSuccess}
              onError={handleEditError}
              onCancel={handleCloseDialogs}
              showHeader={false}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* View Address Dialog (Read-Only) */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>View Address</DialogTitle>
            <DialogDescription>
              {selectedAddress?.isArchived
                ? 'This address is archived and read-only.'
                : 'Address details'}
            </DialogDescription>
          </DialogHeader>
          {selectedAddress && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Address Name</label>
                <p className="text-sm text-muted-foreground">
                  {selectedAddress.addressName}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium">Type</label>
                <p className="text-sm text-muted-foreground">
                  {selectedAddress.addressType}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium">Phone</label>
                <p className="text-sm text-muted-foreground">
                  {selectedAddress.phone || '—'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium">Street</label>
                <p className="text-sm text-muted-foreground">{selectedAddress.street}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">City</label>
                  <p className="text-sm text-muted-foreground">{selectedAddress.city}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">State/Province</label>
                  <p className="text-sm text-muted-foreground">{selectedAddress.state}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Postal Code</label>
                  <p className="text-sm text-muted-foreground">
                    {selectedAddress.postalCode}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium">Country</label>
                  <p className="text-sm text-muted-foreground">
                    {selectedAddress.country}
                  </p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Status</label>
                <div className="mt-1">
                  <Badge variant={selectedAddress.isArchived ? 'secondary' : 'default'}>
                    {selectedAddress.isArchived ? 'Archived' : 'Active'}
                  </Badge>
                </div>
              </div>
              <div className="flex justify-end pt-4">
                <Button variant="outline" onClick={handleCloseDialogs}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
